// MoveTool.jsx (using showToast for enter/exit messages)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { useMapObjects } from "../../../context/MapObjectsContext";
import MoveToolPropertiesPopup from "./MoveToolPropertiesPopup";
import { createMoveToolController } from "../../../utilies/moveTool/MoveToolController";
import { LuArrowUpFromDot } from "react-icons/lu";
import { showToast } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";

function MoveTool() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const editMode = useAppStore((s) => s.editMode);

  const moveToolMode = useAppStore((s) => s.moveToolMode);
  const setMoveToolMode = useAppStore((s) => s.setMoveToolMode);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);

  const { mapObjects, setMapObjects } = useMapObjects();

  const controllerRef = useRef(null);

  const [popupOpen, setPopupOpen] = useState(false);
  const [targetKey, setTargetKey] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);

  const viewer = viewerRef?.current || null;
  const canUse = useMemo(() => Boolean(viewer && viewer.scene && viewer.scene.canvas), [viewer]);

  const prevModeRef = useRef(null); // null => first run guard

  useEffect(() => {
    if (!canUse) return;

    if (!controllerRef.current) {
      controllerRef.current = createMoveToolController({
        viewer,
        mapObjectsGetter: () => mapObjects,
        mapObjectsSetter: setMapObjects,
        onSelectionChange: (count) => setSelectedCount(count),
        originalCameraControls,
      });
    } else {
      controllerRef.current.setMapObjectsGetter(() => mapObjects);
    }
  }, [canUse, viewer, mapObjects]);

  useEffect(() => {
    if (!controllerRef.current) return;

    // avoid showing toast on first mount unless there is an actual toggle
    const prev = prevModeRef.current;
    prevModeRef.current = moveToolMode;

    if (moveToolMode) {
      controllerRef.current.enable();
      if (prev === false) {
        showToast("MoveTool enabled.\nPress these keys to move selected items to the target layer:\nV = VirtualLine\nC = Curbstone\nR = RoadEdge\nL = LaneMarker", Type.NOTIFICATION.SUCCESS, {
          duration: 7500,
          closable: true,
        });
      }
    } else {
      controllerRef.current.disable();
      if (prev === true) {
        showToast("MoveTool disabled.", Type.NOTIFICATION.INFO, {
          duration: 2000,
          closable: true,
        });
      }
    }
  }, [moveToolMode]);

  // ESC: exit mode + full reset
  useEffect(() => {
    if (!moveToolMode) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();

      setPopupOpen(false);
      setTargetKey(null);
      setMoveToolMode(false);
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [moveToolMode, setMoveToolMode]);

  useEffect(() => {
    return () => {
      try {
        controllerRef.current?.destroy();
      } catch {}
      controllerRef.current = null;
    };
  }, []);

  const handleToggleMode = () => {
    if (!canUse) return;
    setMoveToolMode(!moveToolMode);
  };

  const openMovePopup = (key) => {
    if (!controllerRef.current) return;
    if (selectedCount <= 0) return;
    setTargetKey(key);
    setPopupOpen(true);
  };

  const onConfirmMove = async (propsObj) => {
    if (!controllerRef.current || !targetKey) return;

    await controllerRef.current.moveSelectedToTargetLayer({
      targetKey,
      userProps: propsObj,
    });

    setPopupOpen(false);
    setTargetKey(null);
  };

  // Keyboard: V/C/R/L open popup (only when tool is on and not typing in inputs)
  useEffect(() => {
    if (!moveToolMode) return;

    const isTypingTarget = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e) => {
      if (!moveToolMode) return;
      if (popupOpen) return;
      if (selectedCount <= 0) return;
      if (isTypingTarget()) return;
      if (e.repeat) return;

      const k = String(e.key || "").toLowerCase();

      if (k === "v") {
        e.preventDefault();
        openMovePopup("virtualline");
      } else if (k === "c") {
        e.preventDefault();
        openMovePopup("curbstone");
      } else if (k === "r") {
        e.preventDefault();
        openMovePopup("roadedge");
      } else if (k === "l") {
        e.preventDefault();
        openMovePopup("lanemarker");
      }
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [moveToolMode, popupOpen, selectedCount]);

  return (
    <HStack gap={2}>
      <Button
        disabled={editMode}
        variant="solid"
        p={0}
        opacity={1}
        bg={moveToolMode ? "green" : "white"}
        color={moveToolMode ? "white" : "MenuText"}
        borderWidth={moveToolMode ? 2 : 1}
        borderColor={moveToolMode ? "green" : "gray.200"}
        onClick={handleToggleMode}
        aria-pressed={moveToolMode}
      >
        <Tooltip content={moveToolMode ? "MoveTool: ON" : "MoveTool: OFF"}>
          <LuArrowUpFromDot />
        </Tooltip>
      </Button>

      <MoveToolPropertiesPopup
        open={popupOpen}
        onClose={() => {
          setPopupOpen(false);
          setTargetKey(null);
        }}
        targetKey={targetKey}
        mapObjects={mapObjects}
        onConfirm={onConfirmMove}
      />
    </HStack>
  );
}

export default MoveTool;
